import { ngFrameworkLoaded } from "@sazka/ng/ng-integration";
import { useNgSession } from "@sazka/ng/ng-session";
import { sazkaAlert } from "@sazka/utils/sazka-alert";
import { useSessionStorage } from "@sazka/utils/use-storage";
import { watch } from "vue";
import { fixedHelpCobrowsingMode } from "./fixed-help";
import { Loader } from "@sazka/design-system/src/loader/loader";
import { useExponea } from "./use-exponea";
import { isMobileApp, triggerMobileAppEvent } from "@sazka/mobile-apps/mobile-app-integration";
import { logDebugEvent } from "@sazka/utils/logger/use-app-logger";
import { useDWH } from "./use-dwh";

// eslint-disable-next-line
let chatLoaded = false;
let storage: ReturnType<typeof useSessionStorage>;

const loadChatProvider = () => {
    const promise = new Promise<void>((res, rej) => {
        if (chatLoaded) {
            res();
            return;
        }

        const scriptElement = document.createElement("script");
        scriptElement.type = "text/javascript";
        scriptElement.async = true;
        scriptElement.addEventListener("error", () => rej("Nepodařilo se načíst script chatu"), { passive: true });

        const referrer = document.referrer
            ? encodeURIComponent(document.referrer.substring(document.referrer.indexOf("://") + 1))
            : "";
        const location = window.location
            ? encodeURIComponent(window.location.href.substring(window.location.protocol.length))
            : "";

        const chatPath =
            "/index.php/cse/chat/getstatus/(click)/internal/(position)/api/(ma)/br/(top)/350/(units)/pixels/(leaveamessage)/true/(identifier)/prihlaseni/(survey)/1";
        scriptElement.src = `${appSettings["Integrations:Chat:BaseAddress"]}${chatPath}?r="${referrer}&l=${location}`;
        document.body.appendChild(scriptElement);

        // periodically check, if chat is loaded
        const intervalHandle = setInterval(() => {
            if (typeof window.sce_instance !== "undefined") {
                clearInterval(intervalHandle);
                chatLoaded = true;
                res();
            }
        }, 50);
    });

    // do no add another watchers if chat is already loaded
    if (chatLoaded) return;

    promise.then(() => {
        const { session } = useNgSession();

        // notify player login status to chat provider
        watch(session, (newValue, oldValue) => {
            if (!sce_instance.notifySessionChange) return;
            if (newValue?.PlayerID == oldValue?.PlayerID) return; // there are changes in session, which should not trigger notification to chat provider

            const playerId = newValue?.PlayerID || oldValue?.PlayerID;
            sce_instance.notifySessionChange(
                playerId,
                newValue?.PlayerID ? "login" : "logout",
                session.value?.SessionToken || undefined
            );
        });

        sce_instance.onClose(() => {
            storage.removeItem("chat-active");
        });
    });

    return promise;
};

const show = async (isProactiveChat = false, campaignCode?: string, creativeCode?: string) => {
    if (isMobileApp && environment.appName !== "sazkabet") {
        triggerMobileAppEvent("showChat", campaignCode);
        return;
    }

    Loader.show();

    try {
        const promiseNg = ngFrameworkLoaded();
        const promiseChat = loadChatProvider();
        await Promise.all([promiseNg, promiseChat]);
    } catch (e) {
        sazkaAlert(
            "Chat je nedostupný",
            "Omlouváme se, chat je momentálně nedostupný, kontaktujte nás prosím telefonicky na <a href='tel:266121212'>266 121 212</a>."
        );
        return;
    } finally {
        Loader.hide();
    }

    const { session, balanceNumeric, balance, isLoggedIn } = useNgSession();
    const flow: string = environment.nodeAliasPath === "/registrace" ? "NativniReg" : "";

    const player = {
        Brand: "SZK",
        Username: session.value?.PlayerEmail || "",
        PlayerID: session.value?.PlayerID || "",
        FirstName: session.value?.PlayerName || "",
        Gender: "", // we dont have the information
        Language: session.value?.PlayerLanguage || "CZE",
        Balance: (balanceNumeric.value / 100).toFixed(2) || "",
        Currency: balance.value?.Currency || "CZK",
        VipLevel: session.value?.PlayerVipLevel || "",
        PlayMode: "",
        BonusPolicyId: "",
        Depositor: isLoggedIn.value ? "Y" : "N",
        SessionKey: session.value?.SessionToken,
        ProactiveChat: isProactiveChat ? "true" : undefined,
        campaignCode: campaignCode || undefined,
        creativeCode: creativeCode || undefined,
        flow,
    };

    const playerClone = JSON.parse(JSON.stringify(player));
    delete playerClone.SessionKey; // We dont want to log session token, coz safety

    logDebugEvent("deh-general", "show-chat", playerClone);
    sce_instance.showChat(player);
    storage.setItem("chat-active", "true", "essential");
    setupCoBrowsingListener();
};

const registerChatToWindow = () => {
    window.showChat = () => show(false);

    const handleExternalShowChat = (settings: {
        ProactiveChat: string;
        campaignCode: string;
        creativeCode: string;
    }) => {
        show(settings?.ProactiveChat === "true", settings?.campaignCode, settings?.creativeCode);
    };

    useExponea().expose("showChat", handleExternalShowChat);
    useDWH().expose("showChat", handleExternalShowChat);
};

function setupCoBrowsingListener() {
    window.addEventListener(
        "message",
        (event) => {
            if (event.origin != `https:${appSettings["Integrations:Chat:BaseAddress"]}`) return;
            if (event.data?.type === "COMMAND" && event.data?.payload?.command === "SURFLY_START") {
                fixedHelpCobrowsingMode();
            }
        },
        { passive: false }
    );
}

function callShowChat(arg: string = null) {
    show(false, arg);
}

// TODO: Temporary solution, native chat is not implemented yet
// SEE: https://sazka.atlassian.net/browse/FE-2857
// The binding must be here, because circular dependency
(window as any).callShowChat = callShowChat;

export const useChat = () => {
    ngFrameworkLoaded().then(() => {
        ng_hs.onShowChat(() => {
            useChat().show();
        });
    });

    storage = useSessionStorage();
    // follow up on active chat on reload
    if (storage.getItem("chat-active") || appSettings["Kentico:DisableChatLoadOptimalization"] === "True") {
        loadChatProvider();
    }

    return {
        registerChatToWindow,
        show,
    };
};
